import { Routes, Route, Navigate } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { AuthProvider } from "./lib/auth/AuthProvider";
import { RequireAuth } from "./components/RequireAuth";
import { RedirectIfAuthed } from "./components/RedirectIfAuthed";
import { PageErrorBoundary } from "./components/ErrorBoundary";
import { DevLoginRoute, DevLogoutRoute } from "./routes/DevLogin";
import { DashboardPage } from "./routes/Dashboard";
import { DashboardMessagesPage } from "./routes/DashboardMessages";
import { CalendarPage } from "./routes/Calendar";
import { CalendarCyclePage } from "./routes/CalendarCycle";
import { CalendarEventDetailPage } from "./routes/CalendarEventDetail";
import { CalendarEventNewPage } from "./routes/CalendarEventNew";
import { CalendarEventEditPage } from "./routes/CalendarEventEdit";
import { DiariesPage } from "./routes/Diaries";
import { AnniversariesPage } from "./routes/anniversaries";
import { AnniversaryDetailPage } from "./routes/anniversaries/[id]";
import { NewAnniversaryPage } from "./routes/anniversaries/new";
import { EditAnniversaryPage } from "./routes/anniversaries/[id]/edit";
import { PairPage } from "./routes/Pair";
import { ProfilePage } from "./routes/Profile";
import { SettingsPage } from "./routes/Settings";
import { LoginPage } from "./routes/Login";
import { RegisterPage } from "./routes/Register";
import { OnboardingPage } from "./routes/Onboarding";
import { OnboardingProfilePage } from "./routes/OnboardingProfile";
import { ForgotPasswordPage } from "./routes/ForgotPassword";
import ResetPasswordPage from "./routes/ResetPassword";
import { AccountPage } from "./routes/Account";
import { FirstDiaryPage } from "./routes/FirstDiary";
import { NewDiaryPage } from "./routes/NewDiary";
import { InviteLandingPage } from "./routes/InviteLanding";
import { DiaryDetailPage } from "./routes/DiaryDetail";
import { DiaryEditPage } from "./routes/DiaryEdit";
import { PairSettingsPage } from "./routes/PairSettings";
import { DissolvePairPage } from "./routes/DissolvePair";
import { AlbumsPage } from "./routes/Albums";
import { AlbumDetailPage } from "./routes/AlbumDetail";
import { AlbumTrashPage } from "./routes/AlbumTrash";
import { WishesPage } from "./routes/Wishes";
import { WishDetailPage } from "./routes/WishDetail";
import { WishNewPage } from "./routes/WishNew";
import { WishEditPage } from "./routes/WishEdit";
import { WishEditLayoutPage } from "./routes/WishEditLayout";
import { WishArchivePage } from "./routes/WishArchive";
import { WishTrashPage } from "./routes/WishTrash";
import { AnniversaryTrashPage } from "./routes/AnniversaryTrash";

export function App() {
  return (
    <AuthProvider>
      {/* Dev-only auto-login — must be matched BEFORE the catch-all
          ``<Route path="*" />`` below, otherwise the wildcard swallows it.
          Compiled only when import.meta.env.DEV is true; production builds
          strip this branch via Vite dead-code elimination so the dev-login
          path is unreachable in shipped bundles. */}
      <Routes>
        {/* Dev-only auto-login — must be matched BEFORE the catch-all
            ``<Route path="*" />`` below, otherwise the wildcard swallows it.
            Compiled only when import.meta.env.DEV is true; production builds
            strip this branch via Vite dead-code elimination so the dev-login
            path is unreachable in shipped bundles.  Per W2 audit 2026-09-21:
            we gate individual ``<Route>`` elements (NOT a second ``<Routes>``
            block) so the route table stays unified and React Router doesn't
            emit "No routes matched location" false-positive warnings when
            navigating between non-dev paths. */}
        {import.meta.env.DEV && <Route path="/dev-login" element={<DevLoginRoute />} />}
        {import.meta.env.DEV && <Route path="/dev-logout" element={<DevLogoutRoute />} />}

        {/* Public auth routes — no shell */}
        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <PageErrorBoundary label="登入">
                <LoginPage />
              </PageErrorBoundary>
            </RedirectIfAuthed>
          }
        />
        <Route
          path="/register"
          element={
            <RedirectIfAuthed>
              <PageErrorBoundary label="建立帳戶">
                <RegisterPage />
              </PageErrorBoundary>
            </RedirectIfAuthed>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PageErrorBoundary label="忘記密碼">
              <ForgotPasswordPage />
            </PageErrorBoundary>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PageErrorBoundary label="重設密碼">
              <ResetPasswordPage />
            </PageErrorBoundary>
          }
        />
        {/* Anonymous-safe invite landing — pre-fetches /pair-invites/{code}/public */}
        <Route
          path="/invite/:code"
          element={
            <PageErrorBoundary label="邀請連結">
              <InviteLandingPage />
            </PageErrorBoundary>
          }
        />

        {/* Authenticated routes — RequireAuth gate, then AppShell.
         * Every auth-gated `<Route>` wraps its element in <PageErrorBoundary>
         * so a single component throw doesn't take down the entire AppShell.
         * Per iter 13 outcome #4 DoD: "every Route wrapped by ErrorBoundary". */}
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route
            path="/"
            element={
              <PageErrorBoundary label="儀表板">
                <Navigate to="/dashboard" replace />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PageErrorBoundary label="儀表板">
                <DashboardPage />
              </PageErrorBoundary>
            }
          />
          {/* W36.0: the daily-message review archive (spec §2.6). It is a
              child route of /dashboard but its own page — §2.6 R6.4 keeps it
              separate so a year of daily notes is legible without the live
              dashboard chrome around it. */}
          <Route
            path="/dashboard/messages"
            element={
              <PageErrorBoundary label="回顧檔案">
                <DashboardMessagesPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/calendar"
            element={
              <PageErrorBoundary label="日曆">
                <CalendarPage />
              </PageErrorBoundary>
            }
          />
          {/* DESIGN §2 lists the cycle view at this path, distinguished from
              the three screens explicitly marked 「（sheet）」. It was in-page
              state in the first cut, which made it unlinkable and back-button
              inert. Order matters: the literal /calendar/cycle must be
              registered before /calendar/events/:id can shadow it — it cannot,
              since the paths diverge at the second segment, but keeping the
              literal first mirrors the diary / anniversaries ordering. */}
          <Route
            path="/calendar/cycle"
            element={
              <PageErrorBoundary label="週期">
                <CalendarCyclePage />
              </PageErrorBoundary>
            }
          />
          {/* W40.1 F7: the create form became a page, and it is a **literal**
              path under the same prefix as a parameterised sibling. This is the
              third time this project has had to write it down — albums
              (`App.tsx:255-259`) and wishes/anniversaries (`:292-294`) both
              shipped unreachable literal routes before. React Router v6 ranks
              routes by specificity so `/new` would win anyway, but the two
              earlier bugs make the order non-negotiable as a convention: the
              literal is declared above its `:id` sibling, always. */}
          <Route
            path="/calendar/events/new"
            element={
              <PageErrorBoundary label="新增事件">
                <CalendarEventNewPage />
              </PageErrorBoundary>
            }
          />
          {/* Same component, one row loaded first and PATCHed. Declared after
              the literal and after `/calendar/events/:id`, which is the only
              segment they differ at. */}
          <Route
            path="/calendar/events/:id/edit"
            element={
              <PageErrorBoundary label="編輯事件">
                <CalendarEventEditPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/calendar/events/:id"
            element={
              <PageErrorBoundary label="事件詳情">
                <CalendarEventDetailPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/diaries"
            element={
              <PageErrorBoundary label="日記列表">
                <DiariesPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/diaries/new"
            element={
              <PageErrorBoundary label="新增日記">
                <NewDiaryPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/diaries/:id"
            element={
              <PageErrorBoundary label="日記詳情">
                <DiaryDetailPage />
              </PageErrorBoundary>
            }
          />
          {/* W45.0 `ADR-0111` §P4 — editing is a **route**, not a `useState`
              flag inside the detail screen. The claim that makes the PillNav
              disappear is route-scoped (`BottomChrome.tsx:73-74`), so a
              state-gated editor could never make it, and the docked bar lifted
              itself by +72px onto the pill instead. It also means reading a
              diary keeps its navigation, which a claim on the detail screen
              would not. */}
          <Route
            path="/diaries/:id/edit"
            element={
              <PageErrorBoundary label="編輯日記">
                <DiaryEditPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/diaries/trash"
            element={
              <PageErrorBoundary label="垃圾桶">
                <DiariesPage trash />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/anniversaries"
            element={
              <PageErrorBoundary label="紀念日">
                <AnniversariesPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/anniversaries/new"
            element={
              <PageErrorBoundary label="新增紀念日">
                <NewAnniversaryPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/anniversaries/:id/edit"
            element={
              <PageErrorBoundary label="編輯紀念日">
                <EditAnniversaryPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/anniversaries/:id"
            element={
              <PageErrorBoundary label="紀念日詳情">
                <AnniversaryDetailPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/albums"
            element={
              <PageErrorBoundary label="相簿">
                <AlbumsPage />
              </PageErrorBoundary>
            }
          />
          {/* W39.61 — declared ABOVE `/albums/:albumId` on purpose. The router
              matches in declaration order, so a literal path below its
              parameterised sibling is unreachable; the backend had exactly that
              bug and answered 500 rather than 404. The two other trash routes in
              this file are ordered for the same reason. */}
          <Route
            path="/albums/trash"
            element={
              <PageErrorBoundary label="相簿垃圾桶">
                <AlbumTrashPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/albums/:albumId"
            element={
              <PageErrorBoundary label="相簿詳情">
                <AlbumDetailPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/wishes"
            element={
              <PageErrorBoundary label="心願">
                <WishesPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/wishes/archive"
            element={
              <PageErrorBoundary label="心願 archive">
                <WishArchivePage />
              </PageErrorBoundary>
            }
          />
          {/* W39.54 — the literal paths must be declared above `/wishes/:id`
              and `/anniversaries/:id` for the same reason the server's do: a
              sibling `:id` route wins the match and swallows 「trash」 as an id. */}
          <Route
            path="/wishes/trash"
            element={
              <PageErrorBoundary label="心願垃圾桶">
                <WishTrashPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/anniversaries/trash"
            element={
              <PageErrorBoundary label="紀念日垃圾桶">
                <AnniversaryTrashPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/wishes/new"
            element={
              <PageErrorBoundary label="新增心願">
                <WishNewPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/wishes/:id"
            element={
              <PageErrorBoundary label="心願詳情">
                <WishDetailPage />
              </PageErrorBoundary>
            }
          />
          {/* W49.0 §8 — `/wishes/:id/edit`. Declared **above** `/wishes/:id`
              for the same reason `/wishes/trash` is: this project has shipped
              an unreachable literal route three times now (see the note at
              `App.tsx:180-182`). React Router ranks by specificity so both
              match either way, but the declaration order is what keeps a reader
              — and the next person copying this block — from getting it wrong.
              `in-app-link-contract.test.ts` fails on a link with no route. */}
          <Route
            path="/wishes/:id/edit"
            element={
              <PageErrorBoundary label="編輯心願">
                <WishEditPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/wishes/:id/edit-layout"
            element={
              <PageErrorBoundary label="編輯心願">
                <WishEditLayoutPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/pair"
            element={
              <PageErrorBoundary label="配對">
                <PairPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/pair/invite"
            element={
              <PageErrorBoundary label="邀請另一半">
                <PairPage invite />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/profile"
            element={
              <PageErrorBoundary label="個人資料">
                <ProfilePage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/settings"
            element={
              <PageErrorBoundary label="設定">
                <SettingsPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/settings/account"
            element={
              <PageErrorBoundary label="帳號設定">
                <AccountPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/settings/pair"
            element={
              <PageErrorBoundary label="配對設定">
                <PairSettingsPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/settings/pair/dissolve"
            element={
              <PageErrorBoundary label="解散配對">
                <DissolvePairPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/onboarding"
            element={
              <PageErrorBoundary label="設定引導">
                <OnboardingPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/onboarding/profile"
            element={
              <PageErrorBoundary label="完善資料">
                <OnboardingProfilePage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="/onboarding/first-diary"
            element={
              <PageErrorBoundary label="第一篇日記">
                <FirstDiaryPage />
              </PageErrorBoundary>
            }
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AuthProvider>
  );
}
